import { Enemy } from "../../../entities/Enemy.js";
import { Npc } from "../../../entities/Npc.js";
import { Projectile } from "../../../entities/Projectile.js";
import { Effect } from "../../../entities/Effect.js";
import { COMBAT_TUNING } from "../../../data/tuning.js";
import { resolveLocalProjectileRemoteContact } from "../networking/projectileCollision.js";
import { spawnDamageNumber, spawnProjectileImpactEffect, spawnPlayerDamageTaken } from "./visuals.js";

export function applyCombatActions(scene, { aabbOverlap }) {
  const p = scene.playerEntity;
  if (!p || !aabbOverlap) return { playerAttackHitbox: null };

  // ── Spawn throw projectile ──
  if (p.attacking && p.attackType === "throw" && !p.projectileSpawned) {
    const weaponDef = p.getWeaponDef("throw");
    const throwAttack = weaponDef?.attacks?.throw;
    const duration = throwAttack?.duration || 1;
    const progress = p.attackTime / duration;
    if (progress >= throwAttack?.activeWindow?.start) {
      p.projectileSpawned = true;
      const throwFacing = p.throwFacing != null ? p.throwFacing : p.facing;
      const cx = p.x + p.w / 2 + throwFacing * (p.w * COMBAT_TUNING.throwSpawn.forwardFactor);
      const cy = p.y + p.h * 0.28;
      const projConfig = weaponDef?.projectile ? { ...weaponDef.projectile } : null;
      if (projConfig && projConfig.atlas === "food") {
        const idx = Math.floor(Math.random() * 100) + 1;
        projConfig.frame = "icons_28_" + String(idx).padStart(2, "0");
      }
      const throwDamage = throwAttack?.damage || 0;
      const projectile = new Projectile(scene, cx, cy, throwFacing, throwDamage, projConfig, p);
      const spawnToken = `${Date.now()}-${scene.netInputSeq}-${Math.floor(Math.random() * 1e6)}`;
      projectile.pendingNetworkOwnership = true;
      projectile.networkSpawnedAt = scene.time.now;
      projectile.networkSpawnToken = spawnToken;
      scene.netPendingProjectilesByToken.set(spawnToken, projectile);
      projectile.create();
      scene.entities.push(projectile);
      scene.netClient?.sendProjectileSpawn({
        weaponId: weaponDef?.id || "default",
        spawnToken,
        x: cx,
        y: cy,
        facing: throwFacing,
        damage: throwDamage,
        spriteConfig: projConfig,
      });
    }
  }

  // ── Attack hitbox collision ──
  const hitbox = p.getActiveHitbox();
  if (hitbox && !p.attackHitDealt) {
    for (const entity of scene.entities) {
      if (entity === p || entity.dead || entity.dying) continue;
      if (entity instanceof Npc) continue;
      if (aabbOverlap(hitbox, entity.getBounds())) {
        const weaponDef = p.getWeaponDef(p.attackType);
        const attackDef = weaponDef?.attacks?.[p.attackType];
        const remainingAttackMs = attackDef ? Math.max(0, attackDef.duration - p.attackTime) : 0;
        const isEnemyKilled = entity.takeDamage(hitbox.damage, p.facing, "pve");
        if (entity instanceof Enemy && remainingAttackMs > 0) {
          entity.lockAttacks(remainingAttackMs);
        }
        if (entity instanceof Enemy && isEnemyKilled) {
          scene.netClient?.sendScoreEvent?.("enemy_kill");
          scene.applyLocalScoreEvent?.("enemy_kill");
        }
        p.attackHitDealt = true;
        p.hitStopTimer = 65;
        const eb = entity.getBounds();
        const hitX = eb.x + eb.w / 2;
        const hitY = Number.isFinite(entity.y) ? entity.y : eb.y;
        spawnDamageNumber(scene, hitX, hitY, hitbox.damage);
        break;
      }
    }
  }

  // ── Spawn attack effect ──
  if (p.attacking && !p.effectSpawned) {
    const weaponDef = p.getWeaponDef(p.attackType);
    const attack = weaponDef?.attacks?.[p.attackType];
    if (attack?.effect) {
      const progress = p.attackTime / attack.duration;
      if (progress >= attack.activeWindow.start) {
        p.effectSpawned = true;
        if (scene.textures.exists(attack.effect.atlas)) {
          const cx = p.x + p.w / 2 + p.facing * attack.effect.offsetX;
          const cy = p.y + p.h / 2 + attack.effect.offsetY;
          const effect = new Effect(scene, cx, cy, attack.effect.atlas, attack.effect.anim, p.facing === -1);
          scene.effects.push(effect);
        }
      }
    }
  }

  // ── Projectile collision ──
  for (const proj of scene.entities) {
    if (!(proj instanceof Projectile) || proj.markedForRemoval) continue;
    if (proj.isNetworkVisual) continue;
    if (proj.owner === p && resolveLocalProjectileRemoteContact(scene, proj, { aabbOverlap })) continue;
    for (const target of scene.entities) {
      if (target === proj || target.dead || target.dying) continue;
      if (target instanceof Projectile) continue;
      if (target instanceof Npc) continue;
      // Skip owner and same-team entities
      if (proj.owner === target) continue;
      if (proj.owner === p && target === p) continue;
      if (proj.owner instanceof Enemy && target instanceof Enemy) continue;
      // Player projectiles skip the player, enemy projectiles skip enemies
      if (!proj.owner && target === p) continue;
      if (aabbOverlap(proj.getBounds(), target.getBounds())) {
        const isEnemyKilled = target.takeDamage(proj.damage, proj.facing, "pve");
        if (proj.owner === p && target instanceof Enemy && p.attacking) {
          const weaponDef = p.getWeaponDef(p.attackType);
          const attackDef = weaponDef?.attacks?.[p.attackType];
          const remainingAttackMs = attackDef ? Math.max(0, attackDef.duration - p.attackTime) : 0;
          if (remainingAttackMs > 0) target.lockAttacks(remainingAttackMs);
        }
        if (proj.owner === p && target instanceof Enemy && isEnemyKilled) {
          scene.netClient?.sendScoreEvent?.("enemy_kill");
          scene.applyLocalScoreEvent?.("enemy_kill");
        }
        const tb = target.getBounds();
        const hitX = tb.x + tb.w / 2;
        const hitY = (target instanceof Enemy && Number.isFinite(target.y)) ? target.y : tb.y;
        spawnDamageNumber(scene, hitX, hitY, proj.damage);
        spawnProjectileImpactEffect(scene, proj.spriteConfig, proj.x + proj.w / 2, proj.y + proj.h / 2);
        proj.markedForRemoval = true;
        proj.destroy();
        break;
      }
    }
  }

  return { playerAttackHitbox: hitbox };
}

export function applyEnemyCombat(scene, { aabbOverlap }) {
  const p = scene.playerEntity;
  if (!p || !aabbOverlap) return;

  if (p.dead || p.dying || p.hurting) return;

  for (const entity of scene.entities) {
    if (!(entity instanceof Enemy) || entity.dead || entity.dying) continue;
    if (entity.attackLockTimer > 0) continue;
    const enemyHitbox = entity.getActiveHitbox();
    if (!enemyHitbox || entity.attackHitDealt) continue;
    if (aabbOverlap(enemyHitbox, p.getBounds())) {
      const prevHp = p.hp;
      entity.attackHitDealt = true;
      const isLocalPlayerKilled = p.takeDamage(enemyHitbox.damage, entity.facing, "pve");
      if (p.hp < prevHp) {
        const hitX = p.x + p.w / 2;
        const hitY = p.y;
        spawnPlayerDamageTaken(scene, hitX, hitY, enemyHitbox.damage);
      }
      if (isLocalPlayerKilled) {
        scene.netClient?.sendScoreEvent?.("enemy_death");
        scene.applyLocalScoreEvent?.("enemy_death");
      }
    }
  }
}

export function applyCombatWorldUpdates(scene, { dt, dtScale, aabbOverlap }) {
  const p = scene.playerEntity;
  if (!p || dt == null || !Number.isFinite(dt) || !aabbOverlap) return;

  // ── Update other entities ──
  for (const entity of scene.entities) {
    if (entity === p) continue;
    entity.update(dt);
    if ((entity instanceof Enemy || entity instanceof Npc) && !entity.dead && !entity.dying) {
      scene.resolveEntityCollisions(entity, dtScale);
    }
  }

  // ── Update and collect pickups ──
  for (const item of scene.items) {
    if (item.markedForRemoval) continue;
    item.update(dt);
    if (!item.isCollectible?.()) continue;
    if (!p.dead && !p.dying && aabbOverlap(item.getBounds(), p.getBounds())) {
      const prevHp = p.hp;
      item.collect(p);
      const healed = Math.max(0, p.hp - prevHp);
      if (healed > 0 && scene.netClient && scene.netStatus === "connected") {
        scene.netClient.sendHeal(healed);
      }
    }
  }

  scene.effects = scene.effects.filter((e) => !e.markedForRemoval);
}
